import { useEffect, useRef, useState } from "react";

/**
 * The recording's own shape, scrubbed.
 *
 * Replaces a bare `<input type=range>` for playback. A slider says how
 * far through you are and nothing else; a waveform shows where the
 * speech is — which is what a reviewer working through forty dictations
 * actually navigates by. Silence, a restart, a long pause before the
 * impression: all of it is visible, and clicking lands on it.
 *
 * THE RANGE INPUT IS STILL HERE, transparent and on top.
 *
 * That is deliberate and it is the whole accessibility story. A canvas
 * with hand-rolled pointer maths gives up keyboard seeking, focus, and
 * the screen-reader announcement of a position — all of which the
 * native control already does correctly, and none of which is worth
 * rebuilding badly. So the canvas is the picture, `aria-hidden`, and the
 * input is the control. Drag, arrow keys, Home/End and touch all keep
 * working without a line of code.
 *
 * IT DEGRADES TO THE PLAIN SLIDER. Decoding can fail — an unsupported
 * codec, a browser without Web Audio, a recording too large to decode on
 * a modest laptop — and losing the ability to SEEK because a decorative
 * picture failed would be a bad trade. When there are no peaks the input
 * simply renders visibly, exactly as before.
 */

/** Horizontal resolution. More bars than this is invisible at these sizes. */
const BUCKETS = 320;

/**
 * Decode at 8 kHz rather than the file's own rate.
 *
 * `decodeAudioData` resamples to the context's rate, and we are drawing
 * a few hundred bars from the result — the extra samples buy nothing and
 * cost a lot. A ten-minute report at 44.1 kHz is ~106 MB of Float32 in
 * one allocation; at 8 kHz it is ~19 MB. Speech is unaffected at this
 * rate and we are not playing it back, only measuring amplitude.
 */
const DECODE_RATE = 8000;

async function computePeaks(bytes: ArrayBuffer): Promise<Float32Array | null> {
  const Offline =
    window.OfflineAudioContext ??
    (window as unknown as { webkitOfflineAudioContext?: typeof OfflineAudioContext })
      .webkitOfflineAudioContext;
  if (!Offline) return null;
  let ctx: OfflineAudioContext;
  try {
    ctx = new Offline(1, 1, DECODE_RATE);
  } catch {
    return null;
  }
  try {
    // decodeAudioData DETACHES the buffer it is given, and the caller
    // still needs the bytes for the <audio> element. Hand it a copy.
    const buffer = await ctx.decodeAudioData(bytes.slice(0));
    const data = buffer.getChannelData(0);
    const per = Math.max(1, Math.floor(data.length / BUCKETS));
    const peaks = new Float32Array(BUCKETS);
    for (let b = 0; b < BUCKETS; b++) {
      let max = 0;
      const start = b * per;
      const end = Math.min(start + per, data.length);
      for (let i = start; i < end; i++) {
        const v = Math.abs(data[i] as number);
        if (v > max) max = v;
      }
      peaks[b] = max;
    }
    // Normalise to the loudest moment. Dictation is quiet and varies
    // hugely between microphones; drawn against an absolute scale, most
    // real recordings are a flat smear along the middle.
    let loudest = 0;
    for (const p of peaks) if (p > loudest) loudest = p;
    if (loudest > 0) for (let i = 0; i < peaks.length; i++) peaks[i]! /= loudest;
    return peaks;
  } catch {
    return null;
  }
}

export function WaveformScrubber({
  bytes,
  position,
  duration,
  onSeek,
  cy = "audio-scrub",
}: {
  /** The recording's raw bytes, or null before it has been fetched. */
  bytes: ArrayBuffer | null;
  position: number;
  duration: number;
  onSeek: (seconds: number) => void;
  cy?: string;
}) {
  const [peaks, setPeaks] = useState<Float32Array | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!bytes) {
      setPeaks(null);
      return () => {
        cancelled = true;
      };
    }
    void computePeaks(bytes).then((p) => {
      if (!cancelled) setPeaks(p);
    });
    return () => {
      cancelled = true;
    };
  }, [bytes]);

  // Redraw on peaks or position. Not in an animation loop: the playhead
  // moves with `timeupdate`, which fires a few times a second, and
  // spinning rAF for a line that moves that slowly would keep a laptop
  // awake through forty recordings.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !peaks) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const ratio = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (!w || !h) return;
    if (canvas.width !== w * ratio || canvas.height !== h * ratio) {
      canvas.width = w * ratio;
      canvas.height = h * ratio;
    }
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const root = getComputedStyle(document.documentElement);
    const played = root.getPropertyValue("--accent").trim() || "45 106 79";
    const unplayed =
      root.getPropertyValue("--foreground-muted").trim() || "120 120 116";

    const frac = duration > 0 ? Math.min(1, position / duration) : 0;
    const barW = w / peaks.length;
    const mid = h / 2;

    for (let i = 0; i < peaks.length; i++) {
      const x = i * barW;
      // A floor, so silence is a visible thin line rather than a gap.
      // A gap reads as "nothing was recorded here", which for a pause
      // mid-report is the wrong thing to tell a reviewer.
      const amp = Math.max(0.04, peaks[i] as number) * (h / 2) * 0.92;
      ctx.fillStyle = `rgb(${x / w < frac ? played : unplayed})`;
      ctx.fillRect(x, mid - amp, Math.max(1, barW - 0.5), amp * 2);
    }

    // The playhead.
    if (duration > 0) {
      ctx.fillStyle = `rgb(${root.getPropertyValue("--status-urgent").trim() || "194 58 48"})`;
      ctx.fillRect(Math.min(w - 1.5, frac * w), 0, 1.5, h);
    }
  }, [peaks, position, duration]);

  const max = Math.max(duration, 0.1);

  return (
    <div className="relative min-w-[10rem] flex-1">
      {peaks && (
        <canvas
          ref={canvasRef}
          aria-hidden
          data-cy="audio-waveform"
          className="block h-9 w-full rounded"
        />
      )}
      <input
        type="range"
        data-cy={cy}
        min={0}
        max={max}
        step={0.1}
        value={Math.min(position, max)}
        aria-label="Position"
        onChange={(e) => onSeek(Number(e.target.value))}
        className={
          peaks
            ? // Over the picture, invisible, and still the real control:
              // pointer, drag, touch, arrow keys and focus all come from
              // the native element. `accent-transparent` hides its own
              // track and thumb without hiding the element itself, which
              // would take the keyboard with it.
              "absolute inset-0 h-full w-full cursor-pointer opacity-0"
            : "h-1 w-full accent-accent"
        }
      />
    </div>
  );
}
